import React, { useRef, useState } from "react"
import styled from "styled-components"
import fonts from "../../styles/fonts"
import colors from "../../styles/colors"
import breakpoints from "../../styles/breakpoints"
import { graphql, Link, useStaticQuery } from "gatsby"
import Slider from "react-slick"
import { getProductLink } from "../../services/links"
import LeftArrow from "../../resources/img/ui/arrows/arrow-left-green-sm.svg"
import RightArrow from "../../resources/img/ui/arrows/arrow-right-green-sm.svg"
import { getLocalisedPrice } from "../../services/productHelper"
import { withTranslation } from "gatsby-plugin-react-i18next"
import { GatsbyImage } from "gatsby-plugin-image"

const GiftBlockContainer = styled.div`
  background: ${colors.white};
  padding: 22px 26px 28px;
`

const Title = styled.h2`
  ${fonts.selfModern};
  font-size: 14px;
  line-height: 19px;
  color: ${colors.trueBlack};
  padding: 0 36px 0 8px;
  margin-bottom: 18px;
`

const StyledSlider = styled(Slider)`
  width: 100%;
  div {
    outline: none;
  }
`

const Product = styled.div`
  display: grid !important;
  grid-template-columns: auto 1fr;
  padding: 0 22px;
  gap: 23px;

  @media (min-width: ${breakpoints.md}) {
    gap: 32px;
  }
`

const ProductDetail = styled.div`
  ${fonts.helveticaNeueLight};
  color: ${colors.textGrey};
  padding: 12px 0;
  font-size: 10px;
  line-height: 14px;
  margin: 0 0 6px;
  display: flex;
  align-items: center;
  a {
    color: ${colors.textGrey};
  }

  h3 {
    ${fonts.helveticaNeueMedium};
    color: ${colors.black};
    font-size: 12px;
    line-height: 19px;
  }
`

const StyledImage = styled(GatsbyImage)`
  width: 126px;
  height: 126px;

  @media (min-width: ${breakpoints.md}) {
    width: 148px;
    height: 148px;
  }
`

const ImageLink = styled(Link)`
  display: inline-block;
`

const PriceSection = styled.div`
  display: flex;
  padding: 6px 0;
`

const Line = styled.div`
  width: 100%;
  position: absolute;
  width: 130%;
  height: 1px;
  background: ${colors.mediumDarkGrey};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const Price = styled.div`
  ${fonts.helveticaNeueLight};
  font-size: 10px;
  line-height: 12px;
  position: relative;
  color: ${(props) =>
    props.sale
      ? colors.green
      : props.$crossed
      ? colors.mediumDarkGrey
      : colors.black};
  margin-left: ${(props) => props.$crossed && "13px"};
  font-style: ${(props) => (props.$crossed ? "italic" : "none")};
  span {
    ${fonts.helveticaNeueMedium};
  }
  @media (min-width: ${breakpoints.md}) {
    font-size: 12px;
  }
`

const Links = styled.div`
  margin-top: 11px;
  display: grid;
`

const StyledLink = styled(Link)`
  ${fonts.helveticaNeueLight};
  color: ${(props) =>
    props.green ? colors.green : colors.textGrey} !important;
  font-size: 10px;
  line-height: 19px;
  text-decoration: underline;
`

const CollectionCarousel = styled.div`
  position: relative;
`

const StyledButton = styled.button`
  outline: none;
  padding: 0;
  background: transparent;
  border: none;
  display: flex;
  cursor: pointer;
`

const ArrowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  position: absolute;
  top: 50%;
  padding: 0 2px;
  pointer-events: none;
  transform: translateY(-50%);
  img {
    cursor: pointer;
    pointer-events: auto;
  }
`

const GiftBlock = (props) => {
  const { t } = props
  const [slideIndex, setSlideIndex] = useState(0)
  let slider = useRef()
  const langTag = process.env.LANG_TAG
  const isFrance = langTag === "fr-FR"

  const {
    collection,
    productContents,
    productContentsFR,
    collectionContent,
  } = useStaticQuery(
    graphql`
      query {
        collection: shopifyCollection(handle: { eq: "surprise-gift" }) {
          products {
            id
            handle
            title
            description
            tags
            metafields {
              value
              key
            }
            media {
              id
              ... on ShopifyMediaImage {
                image {
                  gatsbyImageData(height: 166)
                }
              }
            }
            priceRangeV2 {
              minVariantPrice {
                amount
                currencyCode
              }
            }
          }
        }
        collectionContent: allContentfulCollectionContent(
          filter: { handle: { eq: "surprise-gift" } }
        ) {
          nodes {
            node_locale
            handle
            urlKey
          }
        }
        productContents: allContentfulProductContent(
          filter: { node_locale: { eq: "en-US" } }
        ) {
          nodes {
            ...ProductCardContentFragment
          }
        }
        productContentsFR: allContentfulProductContent(
          filter: { node_locale: { eq: "fr" } }
        ) {
          nodes {
            ...ProductCardContentFragment
          }
        }
      }
    `
  )

  if (!collection) return null

  let productContentsData

  if (process.env.GATSBY_ACTIVE_ENV === "fr") {
    productContentsData = productContentsFR
  } else {
    productContentsData = productContents
  }

  const localisedCollectionContent = collectionContent.nodes.find((content) => {
    return isFrance
      ? content.node_locale === "fr"
      : content.node_locale !== "fr"
  })

  collection.products.forEach((product) => {
    let content = productContentsData.nodes.find(
      (content) => content.productHandle?.trim() === product.handle
    )

    if (content && content.urlKey) {
      product.handle = content.urlKey
    }
  })

  const next = () => {
    slider.current.slickNext()
  }
  const previous = () => {
    slider.current.slickPrev()
  }

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    fade: false,
    swipe: true,
    swipeToSlide: true,
    draggable: true,
    speed: 400,
    autoplay: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: slideIndex,
    adaptiveHeight: false,
    beforeChange: (current, next) => setSlideIndex(next),
  }

  return (
    <GiftBlockContainer>
      <Title>{t("minicart.gift.title")}</Title>
      <CollectionCarousel>
        <StyledSlider {...settings} ref={slider}>
          {collection.products.map((product, index) => {
            const descriptionFr =
              product.metafields.length > 0 &&
              product.metafields.find(
                (field) => field.key.trim() === "description_fr"
              )
            const description = isFrance
              ? descriptionFr && descriptionFr.value
              : product.description
            let mainImage =
              product.media.length > 1
                ? product?.media[1]
                : product?.media[0]

            const regex = /((?!=|\!|\.).)+(.)/
            let firstParagraph

            if ((firstParagraph = regex.exec(description)) !== null) {
              firstParagraph = firstParagraph[0]
            }

            const productHandle = product.handle.trim()

            return (
              <Product key={index}>
                <div>
                  <ImageLink to={getProductLink(productHandle)}>
                    <StyledImage
                      image={mainImage.image.gatsbyImageData}
                      alt={product.title}
                      objectFit={"contain"}
                    />
                  </ImageLink>
                </div>

                <ProductDetail>
                  <div>
                    <h3>{product.title}</h3>
                    <PriceSection>
                      <Price sale>
                        <span>
                          {getLocalisedPrice(
                            0,
                            product.priceRangeV2.minVariantPrice.currencyCode
                          )}
                        </span>
                      </Price>

                      <Price crossed>
                        <span>
                          {getLocalisedPrice(
                            product.priceRangeV2.minVariantPrice.amount,
                            product.priceRangeV2.minVariantPrice.currencyCode
                          )}
                        </span>
                        <Line />
                      </Price>
                    </PriceSection>
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          firstParagraph.length > 90
                            ? firstParagraph.substring(0, 90) + "..."
                            : firstParagraph,
                      }}
                    />
                    <Links>
                      <StyledLink to={getProductLink(productHandle)} green>
                        {t("minicart.gift.shop_gift")}
                      </StyledLink>
                      <StyledLink to={`/${localisedCollectionContent.urlKey}`}>
                        {t("minicart.gift.shop_collection")}
                      </StyledLink>
                    </Links>
                  </div>
                </ProductDetail>
              </Product>
            )
          })}
        </StyledSlider>
        <ArrowContainer>
          {collection.products.length > 1 && (
            <>
              <StyledButton onClick={() => previous()}>
                <img src={LeftArrow} alt={"previous"} />
              </StyledButton>
              <StyledButton onClick={() => next()}>
                <img src={RightArrow} alt="next" />
              </StyledButton>
            </>
          )}
        </ArrowContainer>
      </CollectionCarousel>
    </GiftBlockContainer>
  )
}

export default withTranslation()(GiftBlock)
